import { yupResolver } from '@hookform/resolvers/yup'
import { useMemo } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { ServiceFormFields } from '../serviceForm/ServiceFormFields'
import { serviceFormSchema, serviceFormValuesOf, toEditProps, type ServiceFormValues } from '../serviceForm/serviceForm'
import { getServiceErrorKey, isNameTaken } from '../serviceErrors'
import { useConfirmationResult } from '../useConfirmationResult'

import { ConfirmationResultModal } from '@percent/lemonade'
import { useMutation } from '@percent/utility'
import { useServices } from '@percent/admin-dashboard/containers/service/ServiceContext'
import type { InternalService } from '@percent/admin-dashboard/api/actions/internalServices/internalServices.types'

type EditServiceModalProps = {
  service: InternalService
  onClose: () => void
  onEdited: () => void
}

export function EditServiceModal({ service, onClose, onEdited }: Readonly<EditServiceModalProps>) {
  const { t } = useTranslation()
  const { internalServiceService } = useServices()
  const [{ isPending }, { mutateAsync: editService }] = useMutation(internalServiceService.editService)
  const result = useConfirmationResult({ onClose, onSuccess: onEdited, isBusy: isPending })
  const schema = useMemo(() => serviceFormSchema(t), [t])
  const form = useForm<ServiceFormValues>({
    defaultValues: serviceFormValuesOf(service),
    mode: 'onChange',
    resolver: yupResolver(schema)
  })
  const [name, ownerTeam, description] = useWatch({
    control: form.control,
    name: ['name', 'ownerTeam', 'description']
  })
  const changes = toEditProps({ name, ownerTeam, description }, service)

  const submit = form.handleSubmit(async values => {
    const edited = toEditProps(values, service)

    if (!edited) return

    try {
      await editService(edited)
      result.succeed()
    } catch (error) {
      if (isNameTaken(error)) {
        form.setError('name', { message: t('internalServices.error.nameTaken') })
      } else {
        result.fail(error)
      }
    }
  })

  return (
    <ConfirmationResultModal
      isOpen
      state={result.state}
      title={t('modal.editService.title')}
      primaryButtonText={t('button.saveChanges')}
      loadingText={t('modal.editService.button.saving')}
      secondaryButtonText={t('button.cancel')}
      onConfirm={submit}
      onClose={result.dismiss}
      onFailureClose={result.backToConfirm}
      disabled={!changes || !form.formState.isValid || isPending}
      loading={isPending}
      primaryBtnTestId="edit-service-submit-button"
      secondaryBtnTestId="edit-service-cancel-button"
      buttonTestId="edit-service-result-button"
      success={{
        title: t('modal.editService.success.title'),
        description: t('modal.editService.success.description', { name: service.name }),
        button: t('button.close')
      }}
      failure={{
        title: t('modal.editService.error.title'),
        description: t(getServiceErrorKey(result.error) ?? 'modal.editService.error.description', {
          name: service.name
        }),
        button: t('button.close')
      }}
    >
      <ServiceFormFields form={form} dataTestId="edit-service" />
    </ConfirmationResultModal>
  )
}
